/* ============================================
   Centralized SVG Icon Library — 1Conecta ERP
   Replaces all emoji usage with consistent SVG icons.
   Usage: import { IconClock, IconCheck } from '@/components/icons/Icons'
   ============================================ */

interface IconProps {
  size?: number
  className?: string
  style?: React.CSSProperties
}

const svgBase = (size: number, className?: string, style?: React.CSSProperties) => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  className,
  style,
})

// ─── Status ───
export const IconClock = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
)

export const IconRefresh = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-1.64-8.36L23 10"/></svg>
)

export const IconCheck = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><polyline points="20 6 9 17 4 12"/></svg>
)

/** Enchufe — integraciones con sistemas de afuera. */
export const IconPlug = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><path d="M12 22v-5"/><path d="M9 7V2"/><path d="M15 7V2"/><path d="M6 13V8a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v5a5 5 0 0 1-5 5h-2a5 5 0 0 1-5-5Z"/></svg>
)

export const IconCheckCircle = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
)

export const IconBan = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>
)

export const IconXCircle = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
)

// ─── Priority ───
export const IconArrowDown = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><line x1="12" y1="5" x2="12" y2="19"/><polyline points="19 12 12 19 5 12"/></svg>
)

export const IconArrowRight = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
)

export const IconArrowUp = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></svg>
)

export const IconAlertCircle = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
)

// ─── Actions ───
export const IconEdit = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
)

export const IconTrash = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
)

export const IconSend = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
)

export const IconDownload = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
)

export const IconUpload = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
)

export const IconPlus = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
)

export const IconUndo = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>
)

export const IconRedo = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.13-9.36L23 10"/></svg>
)

// ─── Objects ───
export const IconCalendar = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
)

export const IconDollar = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
)

export const IconTimer = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
)

export const IconPaperclip = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
)

export const IconMail = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><rect x="2" y="4" width="20" height="16" rx="2"/><polyline points="22,7 12,13 2,7"/></svg>
)

export const IconFile = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
)

export const IconFileText = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
)

export const IconFilePlus = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/></svg>
)

export const IconCamera = ({ size = 14, className, style }: IconProps) => (
  <svg width={size} height={size} className={className} style={style} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
    <circle cx="12" cy="13" r="4" />
  </svg>
)

export const IconImage = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
)

export const IconUser = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
)

// Bot / asistente automatico — la barra del recepcionista en el chat.
export const IconRobot = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><rect x="4" y="8" width="16" height="12" rx="2"/><path d="M12 8V4"/><circle cx="12" cy="3" r="1"/><circle cx="9" cy="13" r="1"/><circle cx="15" cy="13" r="1"/><path d="M9 17h6"/></svg>
)

export const IconPackage = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><line x1="16.5" y1="9.4" x2="7.5" y2="4.21"/><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>
)

// Proyecto / trabajo — usado para vincular un chat a un proyecto.
export const IconBriefcase = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>
)

export const IconTag = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
)

export const IconSearch = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
)

// ─── Navigation ───
export const IconArrowLeft = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
)

export const IconChevronRight = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><polyline points="9 18 15 12 9 6"/></svg>
)

// Menu de acciones secundarias ("mas opciones"). Vertical, para botones de
// barra donde el ancho es lo escaso.
export const IconMoreVertical = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><circle cx="12" cy="5" r="1"/><circle cx="12" cy="12" r="1"/><circle cx="12" cy="19" r="1"/></svg>
)

export const IconChevronDown = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><polyline points="6 9 12 15 18 9"/></svg>
)

export const IconChevronLeft = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><polyline points="15 18 9 12 15 6"/></svg>
)

export const IconChevronsLeft = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><polyline points="11 17 6 12 11 7"/><polyline points="18 17 13 12 18 7"/></svg>
)

export const IconChevronsRight = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><polyline points="13 17 18 12 13 7"/><polyline points="6 17 11 12 6 7"/></svg>
)

export const IconExternalLink = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
)

// ─── Status indicators (filled circles) ───
export const IconCircleFilled = ({ size = 8, className, style }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 8 8" className={className} style={style}>
    <circle cx="4" cy="4" r="4" fill="currentColor"/>
  </svg>
)

// ─── Layout / Kanban ───
export const IconKanban = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="9" y1="3" x2="9" y2="21"/><line x1="15" y1="3" x2="15" y2="21"/></svg>
)

export const IconList = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
)

// ─── Misc ───
export const IconAlertTriangle = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
)

export const IconLock = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
)

export const IconBarChart = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><line x1="12" y1="20" x2="12" y2="10"/><line x1="18" y1="20" x2="18" y2="4"/><line x1="6" y1="20" x2="6" y2="16"/></svg>
)

export const IconMessageSquare = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
)

export const IconEye = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
)

/** Compartir: liberar un borrador interno al panel del partner. */
export const IconShare = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
)

export const IconEyeOff = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
)

// ─── Sprint 0 — New Icons ───
export const IconHeadset = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><path d="M3 18v-6a9 9 0 0 1 18 0v6"/><path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/></svg>
)

export const IconBuilding = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><rect x="4" y="2" width="16" height="20" rx="2"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01M16 6h.01M12 6h.01M8 10h.01M16 10h.01M12 10h.01M8 14h.01M16 14h.01M12 14h.01"/></svg>
)

export const IconMapPin = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
)

export const IconPhone = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
)

export const IconGlobe = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
)

export const IconCreditCard = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
)

export const IconReceipt = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1z"/><line x1="8" y1="8" x2="16" y2="8"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="8" y1="16" x2="12" y2="16"/></svg>
)

export const IconTarget = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>
)

export const IconWallet = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><rect x="2" y="6" width="20" height="14" rx="2"/><path d="M2 10h20"/><path d="M16 14h.01"/></svg>
)

export const IconTrendingUp = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>
)

export const IconFilter = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
)

export const IconColumns = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="12" y1="3" x2="12" y2="21"/></svg>
)

export const IconBell = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
)

export const IconBellOff = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><path d="M13.73 21a2 2 0 0 1-3.46 0"/><path d="M18.63 13A17.89 17.89 0 0 1 18 8"/><path d="M6.26 6.26A5.86 5.86 0 0 0 6 8c0 7-3 9-3 9h14"/><path d="M18 8a6 6 0 0 0-9.33-5"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
)

export const IconMessageCircle = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>
)

export const IconToggle = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><rect x="1" y="5" width="22" height="14" rx="7"/><circle cx="8" cy="12" r="3"/></svg>
)

export const IconZap = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
)

// ─── Chat / Inbox Omnicanal Icons ───
export const IconMicrophone = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
)

export const IconPlay = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><polygon points="5 3 19 12 5 21 5 3"/></svg>
)

export const IconPause = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
)

export const IconStopCircle = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><circle cx="12" cy="12" r="10"/><rect x="9" y="9" width="6" height="6"/></svg>
)

export const IconCheckCheck = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><path d="M18 6L7 17l-5-5"/><path d="M22 10l-11 11-1.5-1.5"/></svg>
)

export const IconMaximize = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
)

export const IconMinimize = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><polyline points="4 14 10 14 10 20"/><polyline points="20 10 14 10 14 4"/><line x1="14" y1="10" x2="21" y2="3"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
)

export const IconVolume2 = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>
)

export const IconX = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
)

export const IconCornerDownLeft = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><polyline points="9 10 4 15 9 20"/><path d="M20 4v7a4 4 0 0 1-4 4H4"/></svg>
)

export const IconReply = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><polyline points="9 14 4 9 9 4"/><path d="M20 20v-7a4 4 0 0 0-4-4H4"/></svg>
)

export const IconWhatsApp = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/><path d="M9 10a.5.5 0 0 0 1 0V9a.5.5 0 0 0-1 0v1zm5 0a.5.5 0 0 0 1 0V9a.5.5 0 0 0-1 0v1z" strokeWidth="0" fill="currentColor"/></svg>
)

export const IconActivity = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
)

// Salir de la sesion — menu de perfil del sidebar.
export const IconLogOut = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
)

export const IconSettings = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
)

// ─── Social Media / Strategy ───
export const IconUsers = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
)

export const IconPalette = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><circle cx="13.5" cy="6.5" r="1.5" fill="currentColor" stroke="none"/><circle cx="17.5" cy="10.5" r="1.5" fill="currentColor" stroke="none"/><circle cx="8.5" cy="7.5" r="1.5" fill="currentColor" stroke="none"/><circle cx="6.5" cy="12" r="1.5" fill="currentColor" stroke="none"/><path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.93 0 1.5-.67 1.5-1.5 0-.39-.15-.74-.39-1.04-.23-.29-.38-.63-.38-1.04 0-.82.68-1.5 1.5-1.5H16c3.31 0 6-2.69 6-6 0-5.17-4.49-9-10-9z"/></svg>
)

export const IconHash = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><line x1="4" y1="9" x2="20" y2="9"/><line x1="4" y1="15" x2="20" y2="15"/><line x1="10" y1="3" x2="8" y2="21"/><line x1="16" y1="3" x2="14" y2="21"/></svg>
)

export const IconLightbulb = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><path d="M9 18h6"/><path d="M10 22h4"/><path d="M12 2a7 7 0 0 0-4 12.7V17h8v-2.3A7 7 0 0 0 12 2z"/></svg>
)

export const IconBot = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><rect x="3" y="11" width="18" height="10" rx="2"/><circle cx="12" cy="5" r="2"/><path d="M12 7v4"/><circle cx="8" cy="16" r="1" fill="currentColor" stroke="none"/><circle cx="16" cy="16" r="1" fill="currentColor" stroke="none"/></svg>
)

export const IconCopy = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
)

export const IconStar = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
)

/* ─── Tema ─────────────────────────────────────────────────────────────────
   Sol y luna del conmutador de tema. El botón muestra SIEMPRE el ícono del
   tema al que se va, no el del tema actual: un control que dibuja el estado
   presente deja al usuario adivinando qué pasa si lo pulsa. */
export const IconSun = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><circle cx="12" cy="12" r="4"/><path d="M12 2v2"/><path d="M12 20v2"/><path d="m4.93 4.93 1.41 1.41"/><path d="m17.66 17.66 1.41 1.41"/><path d="M2 12h2"/><path d="M20 12h2"/><path d="m6.34 17.66-1.41 1.41"/><path d="m19.07 4.93-1.41 1.41"/></svg>
)

export const IconMoon = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
)

/* ─── Infraestructura ──────────────────────────────────────────────────── */
/** Rack de servidor — el servidor físico (WHM), no el servicio que corre en él. */
export const IconServer = ({ size = 14, className, style }: IconProps) => (
  <svg {...svgBase(size, className, style)}><rect x="2" y="2" width="20" height="8" rx="2"/><rect x="2" y="14" width="20" height="8" rx="2"/><line x1="6" y1="6" x2="6.01" y2="6"/><line x1="6" y1="18" x2="6.01" y2="18"/></svg>
)
